import React, { useState, useEffect, useRef } from 'react';
import { Heart, Calendar, MapPin, CheckCircle, Share2, Copy, ExternalLink, Youtube, Music, ImageIcon, Users, Plus, Trash2, Upload, Edit } from '../icons';
import { useAuth } from '../context/AuthContext';
import { useConfirm, usePrompt } from '../components/Modal';
import { getMyWeddingWebsite, updateMyWeddingWebsite, submitPublicRsvp, getWeddings, getFamilyMembers, addFamilyMember, updateFamilyMember, deleteFamilyMember, requestFamilyLimitIncrease, uploadWeddingWebsiteBg } from '../services/api';
import { useNavigate } from 'react-router-dom';
import compressImage from '../utils/compressImage';
import { useLanguage } from '../context/LanguageContext';
import mediaUrl from '../utils/mediaUrl';
import { isNative } from '../utils/native';
import { toast } from 'react-toastify';

function WeddingWebsite() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [confirmDialog, ConfirmDialog] = useConfirm();
  const [promptDialog, PromptDialog] = usePrompt();
  const [website, setWebsite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ our_story: '', event_details: [], youtube_url: '', music_url: '', background_image_url: '' });
  const [saving, setSaving] = useState(false);
  const [rsvpForm, setRsvpForm] = useState({ name: '', email: '', rsvp_status: '', meal_preference: 'Non-vegetarian', adult_count: 1, kids_count: 0 });
  const [rsvpMessage, setRsvpMessage] = useState(null);
  const [rsvpSubmitting, setRsvpSubmitting] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [weddingId, setWeddingId] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [familyLimit, setFamilyLimit] = useState(20);
  const [showAddFamily, setShowAddFamily] = useState(false);
  const [newFamily, setNewFamily] = useState({ name: '', label: '' });
  const [familyPhoto, setFamilyPhoto] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [editFamilyForm, setEditFamilyForm] = useState({ name: '', label: '' });
  const [editFamilyPhoto, setEditFamilyPhoto] = useState(null);
  const [musicPlaying, setMusicPlaying] = useState(false);
  const audioRef = useRef(null);
  const [bgUploading, setBgUploading] = useState(false);
  const bgInputRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    // Only couples can manage the wedding website
    if (user.role !== 'couple') { navigate('/dashboard'); return; }
    loadWebsite();
  }, [user, navigate, authLoading]);

  const loadWebsite = async () => {
    try {
      const res = await getMyWeddingWebsite();
      setWebsite(res.data);
      setEditForm({
        our_story: res.data.our_story || res.data.story || '',
        event_details: Array.isArray(res.data.event_details) ? res.data.event_details : [],
        youtube_url: res.data.youtube_url || '',
        music_url: res.data.music_url || '',
        background_image_url: res.data.background_image_url || '',
      });
      // Load wedding ID and family members
      const wRes = await getWeddings();
      if (wRes.data.length > 0) {
        const wId = wRes.data[0].id;
        setWeddingId(wId);
        try {
          const fRes = await getFamilyMembers(wId);
          setFamilyMembers(fRes.data.members);
          setFamilyLimit(fRes.data.limit);
        } catch {}
      }
    } catch (err) {
      setError(err.response?.data?.error || 'No wedding website found. Please create a wedding first.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateMyWeddingWebsite({
        our_story: editForm.our_story,
        event_details: editForm.event_details,
        youtube_url: editForm.youtube_url,
        music_url: editForm.music_url,
        background_image_url: editForm.background_image_url,
      });
      await loadWebsite();
      setEditing(false);
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleAddFamily = async () => {
    if (!newFamily.name || !newFamily.label) return;
    try {
      const formData = new FormData();
      formData.append('name', newFamily.name);
      formData.append('label', newFamily.label);
      if (familyPhoto) {
        const compressed = await compressImage(familyPhoto);
        formData.append('photo', compressed);
      }
      await addFamilyMember(weddingId, formData);
      setNewFamily({ name: '', label: '' });
      setFamilyPhoto(null);
      setShowAddFamily(false);
      const fRes = await getFamilyMembers(weddingId);
      setFamilyMembers(fRes.data.members);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add family member');
    }
  };

  const handleEditFamily = (member) => {
    setEditingMember(member.id);
    setEditFamilyForm({ name: member.name, label: member.label });
    setEditFamilyPhoto(null);
  };

  const handleSaveEditFamily = async () => {
    if (!editFamilyForm.name || !editFamilyForm.label) return;
    try {
      const formData = new FormData();
      formData.append('name', editFamilyForm.name);
      formData.append('label', editFamilyForm.label);
      if (editFamilyPhoto) {
        const compressed = await compressImage(editFamilyPhoto);
        formData.append('photo', compressed);
      }
      await updateFamilyMember(weddingId, editingMember, formData);
      setEditingMember(null);
      setEditFamilyForm({ name: '', label: '' });
      setEditFamilyPhoto(null);
      const fRes = await getFamilyMembers(weddingId);
      setFamilyMembers(fRes.data.members);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update family member');
    }
  };

  const handleDeleteFamily = async (memberId) => {
    const ok = await confirmDialog({ title: 'Remove Family Member', message: 'Remove this family member?', danger: true, confirmText: 'Remove' });
    if (!ok) return;
    try {
      await deleteFamilyMember(weddingId, memberId);
      setFamilyMembers(familyMembers.filter(m => m.id !== memberId));
    } catch (err) {
      toast.error(err.response?.data?.error || t('weddingWebsite.removeMemberFailed'));
    }
  };

  const handleRequestIncrease = async () => {
    const newLimit = await promptDialog({ title: 'Request Limit Increase', message: `Current limit: ${familyLimit}. Enter new desired limit:`, defaultValue: String(familyLimit + 10), placeholder: 'New limit' });
    if (!newLimit || parseInt(newLimit) <= familyLimit) return;
    try {
      await requestFamilyLimitIncrease(weddingId, { requested_limit: parseInt(newLimit) });
      // Without this the button looked like it did nothing
      toast.success(t('weddingWebsite.requestSent'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('weddingWebsite.requestFailed'));
    }
  };

  const handleBgUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBgUploading(true);
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append('image', compressed);
      const res = await uploadWeddingWebsiteBg(fd);
      const baseUrl = (process.env.REACT_APP_API_URL || '').replace('/api', '');
      const url = res.data.background_image_url.startsWith('/')
        ? baseUrl + res.data.background_image_url
        : res.data.background_image_url;
      setEditForm(f => ({ ...f, background_image_url: url }));
      setWebsite(w => ({ ...w, background_image_url: url }));
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setBgUploading(false);
      e.target.value = '';
    }
  };

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  if (loading) return <div className="loading-page"><div className="spinner"></div></div>;

  if (error) {
    return (
      <div className="wedding-website-page" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Heart size={48} style={{ color: '#be185d', marginBottom: '16px' }} />
          <h2>Wedding Website</h2>
          <p style={{ color: '#6b7280', marginTop: '8px' }}>{error}</p>
          <button className="btn btn-primary" style={{ marginTop: '16px' }} onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const weddingDate = website.wedding_date ? new Date(website.wedding_date) : null;
  const formattedDate = weddingDate
    ? weddingDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : t('weddingWebsite.dateTBD');

  const events = Array.isArray(website.event_details) ? website.event_details : [];
  const storyText = website.our_story || website.story || '';

  const daysUntil = weddingDate ? Math.ceil((weddingDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const months = daysUntil ? Math.floor(daysUntil / 30) : 0;
  const remainingDays = daysUntil ? daysUntil % 30 : 0;

  // In the Android app window.location.origin is https://localhost, which would
  // put a dead link in every WhatsApp/Facebook share; use the real site there.
  const publicOrigin = isNative()
    ? ((process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '') || 'https://nepwed.com')
    : window.location.origin;
  const shareUrl = website.subdomain ? `${publicOrigin}/w/${website.subdomain}` : '';

  const handleCopyLink = () => {
    // clipboard is undefined on plain http and rejects in some WebViews
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(shareUrl))
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch(() => window.prompt(t('weddingWebsite.copyLink'), shareUrl));
  };

  return (
    <div className="wedding-website-page">
      {/* Edit Toggle & Share */}
      <div style={{ position: 'fixed', top: '80px', right: '20px', zIndex: 100, display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
        {!editing ? (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-outline" onClick={() => setEditing(true)} style={{ background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
              ✏️ {t('weddingWebsite.editWebsite')}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? t('common.saving') : '💾 ' + t('common.save')}
            </button>
            <button className="btn btn-outline" onClick={() => setEditing(false)} style={{ background: 'white' }}>{t('common.cancel')}</button>
          </div>
        )}
      </div>

      {/* Share Link Banner */}
      {shareUrl && (
        <div style={{ background: 'linear-gradient(135deg, #fdf2f8, #fce7f3)', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', flexWrap: 'wrap', borderBottom: '1px solid #fbcfe8' }}>
          <Share2 size={18} style={{ color: '#be185d' }} />
          <span style={{ fontWeight: '500', color: '#831843' }}>{t('weddingWebsite.shareLabel')}</span>
          <code style={{ background: 'white', padding: '6px 12px', borderRadius: '6px', fontSize: '0.9rem', border: '1px solid #f9a8d4', color: '#9d174d', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shareUrl}</code>
          <button onClick={handleCopyLink} className="btn btn-primary" style={{ padding: '6px 16px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Copy size={14} /> {linkCopied ? t('common.copied') : t('weddingWebsite.copyLink')}
          </button>
          <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline" style={{ padding: '6px 16px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <ExternalLink size={14} /> {t('weddingWebsite.preview')}
          </a>
          {/* Social share buttons */}
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: '#9d174d', fontWeight: 600 }}>Share:</span>
            <a
              href={`https://wa.me/?text=${encodeURIComponent('You\'re invited to our wedding! ' + shareUrl)}`}
              target="_blank" rel="noopener noreferrer"
              title="Share on WhatsApp"
              style={{ width: 34, height: 34, borderRadius: '50%', background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', boxShadow: '0 2px 6px rgba(0,0,0,0.15)', flexShrink: 0 }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank" rel="noopener noreferrer"
              title="Share on Facebook"
              style={{ width: 34, height: 34, borderRadius: '50%', background: '#1877F2', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', boxShadow: '0 2px 6px rgba(0,0,0,0.15)', flexShrink: 0 }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent('You\'re invited to our wedding! ' + shareUrl)}`}
              target="_blank" rel="noopener noreferrer"
              title="Share on X (Twitter)"
              style={{ width: 34, height: 34, borderRadius: '50%', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', boxShadow: '0 2px 6px rgba(0,0,0,0.15)', flexShrink: 0 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.259 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
            <a
              href={`viber://forward?text=${encodeURIComponent('You\'re invited to our wedding! ' + shareUrl)}`}
              title="Share on Viber"
              style={{ width: 34, height: 34, borderRadius: '50%', background: '#7360F2', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', boxShadow: '0 2px 6px rgba(0,0,0,0.15)', flexShrink: 0 }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M11.994 2C6.472 2 2 6.473 2 11.994c0 2.458.928 4.7 2.45 6.405l-1.604 4.777 4.91-1.578A9.932 9.932 0 0 0 11.994 22C17.516 22 22 17.527 22 12.006 22 6.473 17.516 2 11.994 2zm5.186 14.02c-.223.624-.79 1.14-1.47 1.28-.38.079-.876.143-2.55-.547-2.14-.87-3.52-3.044-3.626-3.185-.1-.14-.85-1.133-.85-2.162 0-1.027.54-1.532.73-1.74a.78.78 0 0 1 .566-.264c.14 0 .28.004.402.01.13.006.303-.05.474.36.177.423.6 1.463.653 1.57.053.106.088.23.018.37-.07.14-.105.228-.21.35-.105.122-.22.272-.316.365-.105.104-.214.217-.092.425.123.208.543.895 1.165 1.449.8.714 1.475.934 1.683 1.04.208.105.33.088.452-.053.122-.14.524-.612.663-.822.14-.21.28-.175.474-.105.194.07 1.232.58 1.44.687.21.105.35.158.4.246.052.088.052.51-.17 1.134z"/></svg>
            </a>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="ww-hero" style={website.background_image_url ? { backgroundImage: `url(${website.background_image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
        <div className="ww-hero-overlay"></div>
        {/* Background image edit button — visible immediately in edit mode */}
        {editing && (
          <div style={{ position: 'absolute', top: '12px', left: '12px', zIndex: 10, display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <input ref={bgInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleBgUpload} />
            <button
              type="button"
              onClick={() => bgInputRef.current?.click()}
              disabled={bgUploading}
              style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', backdropFilter: 'blur(4px)' }}
            >
              <Upload size={14} /> {bgUploading ? 'Uploading...' : (website.background_image_url ? '🔄 Change Background' : '📷 Add Background Image')}
            </button>
            {website.background_image_url && !bgUploading && (
              <button
                type="button"
                onClick={() => { setWebsite(w => ({ ...w, background_image_url: '' })); setEditForm(f => ({ ...f, background_image_url: '' })); }}
                style={{ background: 'rgba(180,0,0,0.7)', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer', fontSize: '13px', backdropFilter: 'blur(4px)' }}
              >✕ Remove</button>
            )}
          </div>
        )}
        <div className="ww-hero-content">
          <div className="ww-names">
            <span className="ww-name">{website.partner1_name || t('weddingWebsite.partner1')}</span>
            <Heart className="ww-heart" size={32} />
            <span className="ww-name">{website.partner2_name || t('weddingWebsite.partner2')}</span>
          </div>
          <div className="ww-date">
            <Calendar size={18} />
            <span>{formattedDate}</span>
          </div>
          <div className="ww-location">
            <MapPin size={18} />
            <span>{website.venue_location || t('weddingWebsite.venueTBD')}, {website.city || 'Nepal'}</span>
          </div>
        </div>
        <div className="ww-scroll-hint">{t('weddingWebsite.scrollStory')} ↓</div>
      </section>

      {/* Custom Background Image — only in edit mode, shown early so it's easy to find */}
      {editing && (
        <section className="ww-section" style={{ background: '#f8fafc', paddingTop: '28px', paddingBottom: '28px' }}>
          <div className="ww-container">
            <h2 className="ww-section-title"><ImageIcon size={24} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} />{t('weddingWebsite.backgroundImage')}</h2>
            <div className="ww-divider"><Heart size={16} /></div>
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#6b7280' }}>Paste an image URL (or use the 📷 button on the hero above)</label>
              <input type="url" value={editForm.background_image_url} onChange={(e) => { setEditForm(f => ({ ...f, background_image_url: e.target.value })); setWebsite(w => ({ ...w, background_image_url: e.target.value })); }}
                placeholder="https://example.com/image.jpg" style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
              {editForm.background_image_url && (
                <div style={{ marginTop: '12px', borderRadius: '8px', overflow: 'hidden', maxHeight: '200px', position: 'relative' }}>
                  <img src={mediaUrl(editForm.background_image_url)} alt="Preview" style={{ width: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
                  <button
                    type="button"
                    onClick={() => { setEditForm(f => ({ ...f, background_image_url: '' })); setWebsite(w => ({ ...w, background_image_url: '' })); }}
                    style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', fontSize: '16px', lineHeight: 1 }}
                  >✕</button>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Our Story */}
      <section className="ww-section ww-story">
        <div className="ww-container">
          <h2 className="ww-section-title">{t('weddingWebsite.ourStory')}</h2>
          <div className="ww-divider">
            <Heart size={16} />
          </div>
          {editing ? (
            <textarea
              value={editForm.our_story}
              onChange={(e) => setEditForm({ ...editForm, our_story: e.target.value })}
              rows={5}
              style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '16px', fontFamily: 'inherit', lineHeight: '1.8' }}
              placeholder={t('weddingWebsite.storyPlaceholder')}
            />
          ) : (
            <p className="ww-story-text">{storyText || t('weddingWebsite.emptyStory')}</p>
          )}
        </div>
      </section>

      {/* Family Members */}
      <section className="ww-section" style={{ background: '#fefce8' }}>
        <div className="ww-container">
          <h2 className="ww-section-title"><Users size={24} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} />{t('weddingWebsite.ourFamily')}</h2>
          <div className="ww-divider"><Heart size={16} /></div>

          {editing && (
            <div style={{ maxWidth: '600px', margin: '0 auto 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>{familyMembers.length} / {familyLimit} {t('weddingWebsite.members')}</span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {familyMembers.length >= familyLimit && (
                    <button className="btn btn-outline" onClick={handleRequestIncrease} style={{ fontSize: '13px', padding: '6px 12px' }}>
                      {t('weddingWebsite.requestMore')}
                    </button>
                  )}
                  {familyMembers.length < familyLimit && (
                    <button className="btn btn-primary" onClick={() => setShowAddFamily(!showAddFamily)} style={{ fontSize: '13px', padding: '6px 12px' }}>
                      <Plus size={14} /> {t('weddingWebsite.addMember')}
                    </button>
                  )}
                </div>
              </div>

              {showAddFamily && (
                <div style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e5e7eb', marginBottom: '16px' }}>
                  <input type="text" placeholder={t('weddingWebsite.namePlaceholder')} value={newFamily.name} onChange={(e) => setNewFamily({ ...newFamily, name: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
                  <input type="text" placeholder={t('weddingWebsite.labelPlaceholder')} value={newFamily.label} onChange={(e) => setNewFamily({ ...newFamily, label: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
                  <input type="file" accept="image/*" onChange={(e) => setFamilyPhoto(e.target.files[0])}
                    style={{ marginBottom: '8px', fontSize: '13px' }} />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-primary" onClick={handleAddFamily} style={{ fontSize: '13px', padding: '6px 16px' }}>
                      <Upload size={14} /> Add
                    </button>
                    <button className="btn btn-outline" onClick={() => setShowAddFamily(false)} style={{ fontSize: '13px', padding: '6px 16px' }}>{t('common.cancel')}</button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '20px', maxWidth: '800px', margin: '0 auto' }}>
            {familyMembers.map((member) => (
              <div key={member.id} style={{ textAlign: 'center', position: 'relative' }}>
                {editingMember === member.id ? (
                  <div style={{ background: 'white', padding: '12px', borderRadius: '12px', border: '1px solid #e5e7eb', textAlign: 'left' }}>
                    <input type="text" value={editFamilyForm.name} onChange={(e) => setEditFamilyForm({ ...editFamilyForm, name: e.target.value })}
                      placeholder="Name" style={{ width: '100%', padding: '6px', marginBottom: '6px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                    <input type="text" value={editFamilyForm.label} onChange={(e) => setEditFamilyForm({ ...editFamilyForm, label: e.target.value })}
                      placeholder="Label" style={{ width: '100%', padding: '6px', marginBottom: '6px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                    <input type="file" accept="image/*" onChange={(e) => setEditFamilyPhoto(e.target.files[0])}
                      style={{ marginBottom: '6px', fontSize: '12px', width: '100%' }} />
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button className="btn btn-primary" onClick={handleSaveEditFamily} style={{ fontSize: '11px', padding: '4px 10px' }}>{t('common.save')}</button>
                      <button className="btn btn-outline" onClick={() => setEditingMember(null)} style={{ fontSize: '11px', padding: '4px 10px' }}>{t('common.cancel')}</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ width: '100px', height: '100px', borderRadius: '50%', margin: '0 auto 8px', overflow: 'hidden', border: '3px solid #fbbf24', background: '#f3f4f6' }}>
                      {member.photo_url ? (
                        <img src={mediaUrl(member.photo_url)} alt={member.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', color: '#9ca3af' }}>
                          {member.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', margin: '0 0 2px', color: '#1f2937' }}>{member.name}</h4>
                    <span style={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>{member.label}</span>
                    {editing && (
                      <div style={{ position: 'absolute', top: '-4px', right: '0', display: 'flex', gap: '4px' }}>
                        <button onClick={() => handleEditFamily(member)}
                          style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '50%', width: '22px', height: '22px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>
                          <Edit size={12} />
                        </button>
                        <button onClick={() => handleDeleteFamily(member.id)}
                          style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: '22px', height: '22px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>

          {familyMembers.length === 0 && !editing && (
            <p style={{ textAlign: 'center', color: '#6b7280' }}>{t('weddingWebsite.familyEmpty')}</p>
          )}
        </div>
      </section>

      {/* Events */}
      <section className="ww-section ww-events">
        <div className="ww-container">
          <h2 className="ww-section-title">{t('weddingWebsite.weddingEvents')}</h2>
          <div className="ww-divider">
            <Heart size={16} />
          </div>
          {editing ? (
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
              {editForm.event_details.map((event, idx) => (
                <div key={idx} style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #e5e7eb' }}>
                  <input value={event.name} onChange={(e) => { const evts = [...editForm.event_details]; evts[idx] = { ...evts[idx], name: e.target.value }; setEditForm({ ...editForm, event_details: evts }); }}
                    placeholder={t('weddingWebsite.eventNamePlaceholder')} style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                    <input type="date" value={event.date || ''} onChange={(e) => { const evts = [...editForm.event_details]; evts[idx] = { ...evts[idx], date: e.target.value }; setEditForm({ ...editForm, event_details: evts }); }}
                      style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }} />
                    <input value={event.time || ''} onChange={(e) => { const evts = [...editForm.event_details]; evts[idx] = { ...evts[idx], time: e.target.value }; setEditForm({ ...editForm, event_details: evts }); }}
                      placeholder="Time (e.g. 4:00 PM)" style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }} />
                  </div>
                  <input value={event.venue || ''} onChange={(e) => { const evts = [...editForm.event_details]; evts[idx] = { ...evts[idx], venue: e.target.value }; setEditForm({ ...editForm, event_details: evts }); }}
                    placeholder={t('weddingWebsite.venuePlaceholder')} style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
                  <input value={event.description || ''} onChange={(e) => { const evts = [...editForm.event_details]; evts[idx] = { ...evts[idx], description: e.target.value }; setEditForm({ ...editForm, event_details: evts }); }}
                    placeholder={t('weddingWebsite.descriptionPlaceholder')} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
                  <button onClick={() => { const evts = editForm.event_details.filter((_, i) => i !== idx); setEditForm({ ...editForm, event_details: evts }); }}
                    style={{ marginTop: '8px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px' }}>{t('weddingWebsite.removeEvent')}</button>
                </div>
              ))}
              <button onClick={() => setEditForm({ ...editForm, event_details: [...editForm.event_details, { name: '', date: '', time: '', venue: '', description: '' }] })}
                className="btn btn-outline" style={{ width: '100%' }}>{t('weddingWebsite.addEvent')}</button>
            </div>
          ) : (
            <div className="ww-events-list">
              {events.length > 0 ? events.map((event, idx) => (
                <div key={idx} className="ww-event-card">
                  <div className="ww-event-icon">
                    {idx === 0 ? '✋' : idx === 1 ? '💒' : '🎉'}
                  </div>
                  <h3>{event.name}</h3>
                  <div className="ww-event-details">
                    <span><Calendar size={14} /> {event.date ? new Date(event.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : t('weddingWebsite.dateTBD')} {event.time ? `at ${event.time}` : ''}</span>
                    <span><MapPin size={14} /> {event.venue || t('weddingWebsite.venueTBD')}</span>
                  </div>
                  <p>{event.description}</p>
                </div>
              )) : (
                <p style={{ textAlign: 'center', color: '#6b7280' }}>{t('weddingWebsite.noEventsYet')}</p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Countdown */}
      {daysUntil && daysUntil > 0 && (
        <section className="ww-section ww-countdown">
          <div className="ww-container">
            <h2 className="ww-section-title">{t('weddingWebsite.countingDown')}</h2>
            <div className="ww-countdown-grid">
              <div className="countdown-unit">
                <span className="countdown-num">{months}</span>
                <span className="countdown-lbl">{t('weddingWebsite.months')}</span>
              </div>
              <div className="countdown-unit">
                <span className="countdown-num">{remainingDays}</span>
                <span className="countdown-lbl">{t('weddingWebsite.days')}</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* YouTube / Live Stream */}
      {(editing || website.youtube_url) && (
        <section className="ww-section" style={{ background: '#fdf2f8' }}>
          <div className="ww-container">
            <h2 className="ww-section-title"><Youtube size={24} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} />{t('weddingWebsite.liveStream')}</h2>
            <div className="ww-divider"><Heart size={16} /></div>
            {editing ? (
              <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>{t('weddingWebsite.youtubeUrl')}</label>
                <input type="url" value={editForm.youtube_url} onChange={(e) => setEditForm({ ...editForm, youtube_url: e.target.value })}
                  placeholder="https://youtube.com/watch?v=..." style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
            ) : (
              getYoutubeEmbedUrl(website.youtube_url) && (
                <div style={{ maxWidth: '720px', margin: '0 auto', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                  <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                    <iframe src={getYoutubeEmbedUrl(website.youtube_url)} title="Wedding Video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} />
                  </div>
                </div>
              )
            )}
          </div>
        </section>
      )}

      {/* Background Music */}
      {(editing || website.music_url) && (
        <section className="ww-section" style={{ background: '#f0fdf4' }}>
          <div className="ww-container">
            <h2 className="ww-section-title"><Music size={24} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} />{t('weddingWebsite.backgroundMusic')}</h2>
            <div className="ww-divider"><Heart size={16} /></div>
            {editing ? (
              <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>{t('weddingWebsite.musicUrl')}</label>
                <input type="url" value={editForm.music_url} onChange={(e) => setEditForm({ ...editForm, music_url: e.target.value })}
                  placeholder="https://example.com/song.mp3" style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
            ) : (
              <div style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center' }}>
                <audio ref={audioRef} src={website.music_url} loop />
                <button className="btn btn-primary" onClick={() => {
                  if (audioRef.current) {
                    if (musicPlaying) { audioRef.current.pause(); } else { audioRef.current.play(); }
                    setMusicPlaying(!musicPlaying);
                  }
                }} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 28px', borderRadius: '50px' }}>
                  <Music size={18} /> {musicPlaying ? '⏸ ' + t('weddingWebsite.pauseMusic') : '▶ ' + t('weddingWebsite.playMusic')}
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* RSVP */}
      <section className="ww-section ww-rsvp">
        <div className="ww-container">
          <h2 className="ww-section-title">{t('weddingWebsite.rsvp')}</h2>
          <div className="ww-divider">
            <Heart size={16} />
          </div>
          <p>{t('weddingWebsite.rsvpHonored')}</p>
          {rsvpMessage && (
            <div style={{
              padding: '16px 20px', borderRadius: '12px', marginBottom: '20px', maxWidth: '500px', margin: '0 auto 20px',
              background: rsvpMessage.type === 'success' ? '#f0fdf4' : '#fef2f2',
              color: rsvpMessage.type === 'success' ? '#166534' : '#991b1b',
              border: `1px solid ${rsvpMessage.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
              display: 'flex', alignItems: 'center', gap: '10px'
            }}>
              {rsvpMessage.type === 'success' && <CheckCircle size={20} />}
              {rsvpMessage.text}
            </div>
          )}
          <form className="ww-rsvp-form" onSubmit={async (e) => {
            e.preventDefault();
            if (!rsvpForm.name || !rsvpForm.email || !rsvpForm.rsvp_status) return;
            setRsvpSubmitting(true);
            setRsvpMessage(null);
            try {
              const nameParts = rsvpForm.name.trim().split(' ');
              const firstName = nameParts[0];
              const lastName = nameParts.slice(1).join(' ');
              const res = await submitPublicRsvp({
                wedding_id: website.wedding_id,
                first_name: firstName,
                last_name: lastName,
                email: rsvpForm.email,
                rsvp_status: rsvpForm.rsvp_status,
                meal_preference: rsvpForm.meal_preference,
                adult_count: rsvpForm.rsvp_status === 'accepted' ? rsvpForm.adult_count : 0,
                kids_count: rsvpForm.rsvp_status === 'accepted' ? rsvpForm.kids_count : 0
              });
              const successText = res.data?.isUpdate
                ? (t('weddingWebsite.rsvpUpdated') || 'Your RSVP has been updated!')
                : t('weddingWebsite.rsvpSuccess');
              setRsvpMessage({ type: 'success', text: successText });
              setRsvpForm({ name: '', email: '', rsvp_status: '', meal_preference: 'Non-vegetarian', adult_count: 1, kids_count: 0 });
            } catch (err) {
              setRsvpMessage({ type: 'error', text: err.response?.data?.error || t('weddingWebsite.rsvpError') });
            } finally {
              setRsvpSubmitting(false);
            }
          }}>
            <input type="text" placeholder={t('weddingWebsite.yourFullName')} required value={rsvpForm.name} onChange={(e) => setRsvpForm({ ...rsvpForm, name: e.target.value })} />
            <input type="email" placeholder={t('weddingWebsite.emailAddress')} required value={rsvpForm.email} onChange={(e) => setRsvpForm({ ...rsvpForm, email: e.target.value })} />
            <select required value={rsvpForm.rsvp_status} onChange={(e) => setRsvpForm({ ...rsvpForm, rsvp_status: e.target.value })}>
              <option value="">{t('weddingWebsite.willYouAttend')}</option>
              <option value="accepted">{t('weddingWebsite.joyfullyAccept')}</option>
              <option value="declined">{t('weddingWebsite.regretfullyDecline')}</option>
              <option value="maybe">{t('weddingWebsite.notSureYet')}</option>
            </select>
            <select value={rsvpForm.meal_preference} onChange={(e) => setRsvpForm({ ...rsvpForm, meal_preference: e.target.value })}>
              <option value="Non-vegetarian">{t('weddingWebsite.nonVeg')}</option>
              <option value="Vegetarian">{t('weddingWebsite.vegetarian')}</option>
              <option value="Vegan">{t('weddingWebsite.vegan')}</option>
            </select>
            {rsvpForm.rsvp_status === 'accepted' && (
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#374151' }}>{t('weddingWebsite.adults') || 'Adults'}</label>
                  <input type="number" min="1" max="20" value={rsvpForm.adult_count} onChange={(e) => setRsvpForm({ ...rsvpForm, adult_count: parseInt(e.target.value) || 1 })} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#374151' }}>{t('weddingWebsite.kids') || 'Kids'}</label>
                  <input type="number" min="0" max="20" value={rsvpForm.kids_count} onChange={(e) => setRsvpForm({ ...rsvpForm, kids_count: parseInt(e.target.value) || 0 })} />
                </div>
              </div>
            )}
            <button type="submit" className="btn btn-primary btn-lg" disabled={rsvpSubmitting}>
              {rsvpSubmitting ? t('common.sending') : t('weddingWebsite.sendRsvp')}
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <div className="ww-footer">
        <Heart size={20} />
        <p>{website.partner1_name} & {website.partner2_name}</p>
        <span>{formattedDate} • {website.city || 'Nepal'}</span>
        <small>{t('weddingWebsite.poweredBy')}</small>
      </div>
      {ConfirmDialog}
      {PromptDialog}
    </div>
  );
}

export default WeddingWebsite;
